import { useAnimationFrameLoop } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, HTMLAttributes } from 'react'
import { useCallback, useRef } from 'react'
import { AudioController } from './engine/audio-controller.js'
import { getDBPercentage } from './lib/db.js'
import styles from './meter.module.css'

export interface MeterProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  controller?: AudioController
  at?: 'pre' | 'post'
  color?: 'primary' | 'secondary'
  orientation?: 'horizontal' | 'vertical'
}

export const Meter: FC<MeterProps> = props => {
  const { controller, at = 'pre', color = 'secondary', className, orientation = 'horizontal', ...restProps } = props

  const ref = useRef<HTMLDivElement | null>(null)
  const updateMeter = useCallback(() => {
    if (!(controller && ref.current)) {
      return
    }
    const db = controller.getDB(at)
    const size = `${getDBPercentage({ value: db.peak, reference: 0 })}%`
    if (orientation === 'horizontal') {
      ref.current.style.width = size
    } else {
      ref.current.style.height = size
    }
  }, [at, controller, orientation])

  useAnimationFrameLoop(updateMeter)

  if (!controller) {
    return null
  }

  /** We need this to be performant, so we don't keep state in React here */
  return (
    <div
      className={cn(
        styles.meter,
        orientation === 'horizontal' ? styles['meter--horizontal'] : styles['meter--vertical'],
        color === 'primary' ? styles['meter--primary'] : styles['meter--secondary'],
        className,
      )}
      ref={ref}
      {...restProps}
    />
  )
}

export default Meter
